import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { vendorsService } from '../services/payables/vendors';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaHistory, FaMoneyBillWave, FaFileInvoice, FaFilter } from 'react-icons/fa';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CreateVendorModal from '../components/vendors/CreateVendorModal';
import EditVendorModal from '../components/vendors/EditVendorModal';
import VendorDetailModal from '../components/vendors/VendorDetailModal';
import VendorPaymentHistoryModal from '../components/vendors/VendorPaymentHistoryModal';
import { formatCurrency } from '../utils/formatting';

const Vendors = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [summary, setSummary] = useState({
    totalVendors: 0,
    activeVendors: 0,
    inactiveVendors: 0,
    totalOutstanding: 0
  });

  useEffect(() => {
    fetchVendors();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const vendorData = await vendorsService.getVendors(user.companyId);
      setVendors(vendorData);
      calculateSummary(vendorData);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(t('common.error_loading_data'));
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (vendorData) => {
    const summary = {
      totalVendors: vendorData.length,
      activeVendors: vendorData.filter(v => v.status === 'active').length,
      inactiveVendors: vendorData.filter(v => v.status === 'inactive').length,
      totalOutstanding: vendorData.reduce((sum, v) => sum + (v.outstanding || 0), 0)
    };
    setSummary(summary);
  };

  const handleCreateVendor = async (vendorData) => {
    try {
      await vendorsService.addVendor({
        ...vendorData,
        companyId: user.companyId,
        createdBy: user.uid
      });
      setShowCreateModal(false);
      fetchVendors();
    } catch (err) {
      console.error('Error creating vendor:', err);
      setError(t('common.error'));
    }
  };

  const handleEditVendor = async (vendorData) => {
    try {
      await vendorsService.updateVendor(selectedVendor.id, {
        ...vendorData,
        updatedBy: user.uid
      });
      setShowEditModal(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (err) {
      console.error('Error updating vendor:', err);
      setError(t('common.error'));
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (window.confirm(t('common.confirm_delete'))) {
      try {
        await vendorsService.deleteVendor(vendorId);
        fetchVendors();
      } catch (err) {
        console.error('Error deleting vendor:', err);
        setError(t('common.error'));
      }
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('vendors.title')}</h1>
        <p className="text-gray-600 mt-1">{t('vendors.vendor_details')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FaPlus className="w-4 h-4" />
              {t('vendors.create_vendor')}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t('vendors.total_vendors')}</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalVendors}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FaFileInvoice className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t('common.active')}</p>
              <p className="text-2xl font-bold text-green-600">{summary.activeVendors}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FaFileInvoice className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t('common.inactive')}</p>
              <p className="text-2xl font-bold text-red-600">{summary.inactiveVendors}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <FaFileInvoice className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">{t('vendors.outstanding_amount')}</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(summary.totalOutstanding)}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <FaMoneyBillWave className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">{t('common.all_statuses')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
            <FaFilter className="w-4 h-4" />
            {t('common.filter')}
          </button>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left p-4 font-medium text-gray-900">{t('vendors.vendor_name')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('clients.email')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('clients.phone')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('vendors.payment_terms')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('vendors.outstanding_amount')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('common.status')}</th>
                <th className="text-left p-4 font-medium text-gray-900">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendors.map((vendor) => (
                <tr key={vendor.id} className="border-b hover:bg-gray-50">
                  <td className="p-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {vendor.name || vendor.companyName}
                      </div>
                      {vendor.vendorCode && (
                        <div className="text-sm text-gray-500">
                          {t('vendors.vendor_type')}: {vendor.vendorCode}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-gray-900">{vendor.email || '-'}</td>
                  <td className="p-4 text-gray-900">{vendor.phone || '-'}</td>
                  <td className="p-4 text-gray-900">{vendor.paymentTerms || '-'}</td>
                  <td className="p-4">
                    <span className={`font-medium ${(vendor.balance || vendor.outstanding || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(Math.abs(vendor.balance || vendor.outstanding || 0))}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      vendor.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {vendor.status === 'active' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedVendor(vendor);
                          setShowDetailModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title={t('common.view')}
                      >
                        <FaEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedVendor(vendor);
                          setShowEditModal(true);
                        }}
                        className="text-gray-600 hover:text-gray-800 transition-colors"
                        title={t('common.edit')}
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedVendor(vendor);
                          setShowPaymentHistoryModal(true);
                        }}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title={t('vendors.last_payment')}
                      >
                        <FaHistory className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVendor(vendor.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title={t('common.delete')}
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredVendors.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? t('common.no_matching_results') 
                : t('common.noData')}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateVendorModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateVendor}
        />
      )}

      {showEditModal && selectedVendor && (
        <EditVendorModal
          vendor={selectedVendor}
          onClose={() => {
            setShowEditModal(false);
            setSelectedVendor(null);
          }}
          onSubmit={handleEditVendor}
        />
      )}

      {showDetailModal && selectedVendor && (
        <VendorDetailModal
          vendor={selectedVendor}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVendor(null);
          }}
        />
      )}

      {showPaymentHistoryModal && selectedVendor && (
        <VendorPaymentHistoryModal
          vendor={selectedVendor}
          onClose={() => {
            setShowPaymentHistoryModal(false);
            setSelectedVendor(null);
          }}
        />
      )}
    </div>
  );
};

export default Vendors; 